import React, {useState} from 'react';
import {useParams, Link, Navigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {getProjectBySlug, getRelatedProjects} from '../data/projects';

const {FiArrowLeft, FiExternalLink, FiCalendar, FiMapPin, FiClock, FiUsers, FiCheckCircle, FiTrendingUp, FiArrowRight, FiZoomIn, FiX} = FiIcons;

const ProjectDetail = () => {
  const {slug} = useParams();
  const [selectedImage, setSelectedImage] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  const project = getProjectBySlug(slug);

  const handleImageClick = (index) => {
    setSelectedImage(index);
    setShowLightbox(true);
  };

  const closeLightbox = () => {
    setShowLightbox(false);
  };

  const nextImage = () => {
    if (project && project.gallery) {
      setSelectedImage((prev) => (prev + 1) % project.gallery.length);
    }
  };

  const prevImage = () => {
    if (project && project.gallery) {
      setSelectedImage((prev) => (prev - 1 + project.gallery.length) % project.gallery.length);
    }
  };

  // Handle keyboard navigation - MOVED BEFORE CONDITIONAL RETURN
  React.useEffect(() => {
    const handleKeyPress = (e) => {
      if (!showLightbox) return;
      
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showLightbox, project]); // Added project as dependency

  // Conditional return AFTER hooks
  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  const relatedProjects = getRelatedProjects(project.id, project.category);

  return (
    <div className="min-h-screen bg-white">
      {/* Back to Projects Button */}
      <div className="fixed top-24 left-8 z-40">
        <Link
          to="/projects"
          className="flex items-center space-x-2 bg-white/95 backdrop-blur-md text-slate-700 px-4 py-2 rounded-full shadow-lg hover:bg-white hover:shadow-xl transition-all duration-300 border border-slate-200"
        >
          <SafeIcon icon={FiArrowLeft} className="text-sm" />
          <span className="font-medium">Back to Projects</span>
        </Link>
      </div>

      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900 overflow-hidden pt-32">
        <div className="absolute inset-0 opacity-20">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-white"
          >
            <div className="mb-6">
              <span className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                {project.category}
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {project.title}
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mb-8">
              {project.description}
            </p>

            {/* Project Meta */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiCalendar} className="text-orange-400" />
                <div>
                  <div className="text-sm text-slate-400">Year</div>
                  <div className="font-semibold">{project.year}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiMapPin} className="text-orange-400" />
                <div>
                  <div className="text-sm text-slate-400">Location</div>
                  <div className="font-semibold">{project.location}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiClock} className="text-orange-400" />
                <div>
                  <div className="text-sm text-slate-400">Duration</div>
                  <div className="font-semibold">{project.duration}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiUsers} className="text-orange-400" />
                <div>
                  <div className="text-sm text-slate-400">Client</div>
                  <div className="font-semibold">{project.client}</div>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              {project.url && (
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-orange-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-orange-600 transition-all duration-300 inline-flex items-center justify-center space-x-3"
                >
                  <span>View Live Site</span>
                  <SafeIcon icon={FiExternalLink} className="group-hover:translate-x-1 transition-transform duration-300" />
                </a>
              )}
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-slate-900 transition-all duration-300 inline-flex items-center justify-center"
              >
                Start Your Project
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Project Gallery */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Project Gallery</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {project.gallery.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{opacity: 0, scale: 0.9}}
                  whileInView={{opacity: 1, scale: 1}}
                  transition={{duration: 0.6, delay: index * 0.1}}
                  viewport={{once: true}}
                  className="group relative cursor-pointer overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  onClick={() => handleImageClick(index)}
                >
                  <img
                    src={image}
                    alt={`${project.title} - Image ${index + 1}`}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/20 transition-all duration-300 flex items-center justify-center">
                    <SafeIcon icon={FiZoomIn} className="text-white opacity-0 group-hover:opacity-100 text-2xl transition-opacity duration-300" />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Challenge */}
              <motion.div
                initial={{opacity: 0, y: 30}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.8}}
                viewport={{once: true}}
              >
                <h3 className="text-2xl font-bold text-slate-900 mb-6">The Challenge</h3>
                <p className="text-lg text-slate-600 leading-relaxed">{project.challenge}</p>
              </motion.div>

              {/* Solution */}
              <motion.div
                initial={{opacity: 0, y: 30}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.8, delay: 0.2}}
                viewport={{once: true}}
              >
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Our Solution</h3>
                <p className="text-lg text-slate-600 leading-relaxed">{project.solution}</p>
              </motion.div>

              {/* Results */}
              <motion.div
                initial={{opacity: 0, y: 30}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.8, delay: 0.4}}
                viewport={{once: true}}
              >
                <h3 className="text-2xl font-bold text-slate-900 mb-6">
                  <SafeIcon icon={FiTrendingUp} className="inline mr-3 text-orange-500" />
                  Results & Impact
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {project.results.map((result, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-slate-700 leading-relaxed">{result}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Features */}
              <motion.div
                initial={{opacity: 0, y: 30}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.8, delay: 0.6}}
                viewport={{once: true}}
              >
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Key Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                      <SafeIcon icon={FiCheckCircle} className="text-orange-500 flex-shrink-0" />
                      <span className="text-slate-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Project Info */}
              <motion.div
                initial={{opacity: 0, y: 30}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.8}}
                viewport={{once: true}}
                className="bg-slate-50 p-6 rounded-xl"
              >
                <h4 className="text-lg font-bold text-slate-900 mb-4">Project Details</h4>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-slate-500 mb-1">Services</div>
                    <div className="flex flex-wrap gap-2">
                      {project.services.map((service, index) => (
                        <span
                          key={index}
                          className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-500 mb-1">Technologies</div>
                    <div className="flex flex-wrap gap-2">
                      {project.techStack.map((tech, index) => (
                        <span
                          key={index}
                          className="bg-slate-200 text-slate-700 px-3 py-1 rounded-full text-sm"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Testimonial */}
              {project.testimonial && (
                <motion.div
                  initial={{opacity: 0, y: 30}}
                  whileInView={{opacity: 1, y: 0}}
                  transition={{duration: 0.8, delay: 0.2}}
                  viewport={{once: true}}
                  className="bg-orange-50 p-6 rounded-xl border-l-4 border-orange-500"
                >
                  <div className="text-orange-600 mb-3">
                    ★★★★★
                  </div>
                  <blockquote className="text-slate-700 italic mb-4">
                    "{project.testimonial.content}"
                  </blockquote>
                  <div>
                    <div className="font-semibold text-slate-900">{project.testimonial.author}</div>
                    <div className="text-slate-600 text-sm">{project.testimonial.role}</div>
                  </div>
                </motion.div>
              )}

              {/* CTA */}
              <motion.div
                initial={{opacity: 0, y: 30}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.8, delay: 0.4}}
                viewport={{once: true}}
                className="bg-slate-900 text-white p-6 rounded-xl"
              >
                <h4 className="text-lg font-bold mb-4">Like This Project?</h4>
                <p className="text-slate-300 mb-6">
                  Let's discuss how we can create something similar for your business.
                </p>
                <Link
                  to="/contact"
                  className="w-full bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors duration-300 inline-flex items-center justify-center space-x-2"
                >
                  <span>Get Started</span>
                  <SafeIcon icon={FiArrowRight} />
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.8}}
              viewport={{once: true}}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Related Projects</h2>
              <p className="text-slate-600">Explore more projects in the {project.category.toLowerCase()} category</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {relatedProjects.map((relatedProject, index) => (
                <motion.div
                  key={relatedProject.id}
                  initial={{opacity: 0, y: 30}}
                  whileInView={{opacity: 1, y: 0}}
                  transition={{duration: 0.6, delay: index * 0.2}}
                  viewport={{once: true}}
                  className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={relatedProject.image}
                      alt={relatedProject.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        {relatedProject.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{relatedProject.title}</h3>
                    <p className="text-slate-600 mb-4">{relatedProject.shortDescription}</p>
                    <Link
                      to={`/projects/${relatedProject.slug}`}
                      className="inline-flex items-center space-x-2 text-orange-600 hover:text-orange-700 font-medium"
                    >
                      <span>View Project</span>
                      <SafeIcon icon={FiArrowRight} />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Image Lightbox */}
      {showLightbox && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          {/* Close Button - Fixed in top right corner */}
          <button
            onClick={closeLightbox}
            className="fixed top-6 right-6 z-60 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-full flex items-center justify-center text-xl font-bold transition-all duration-300 hover:scale-110"
          >
            <SafeIcon icon={FiX} />
          </button>

          {/* Navigation Arrows */}
          {project.gallery && project.gallery.length > 1 && (
            <>
              <button
                onClick={(e) => {e.stopPropagation(); prevImage();}}
                className="fixed left-6 top-1/2 transform -translate-y-1/2 z-60 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-full flex items-center justify-center text-xl transition-all duration-300 hover:scale-110"
              >
                ‹
              </button>
              <button
                onClick={(e) => {e.stopPropagation(); nextImage();}}
                className="fixed right-6 top-1/2 transform -translate-y-1/2 z-60 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-full flex items-center justify-center text-xl transition-all duration-300 hover:scale-110"
              >
                ›
              </button>
            </>
          )}

          {/* Image Container */}
          <div 
            className="relative max-w-6xl max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={project.gallery[selectedImage]}
              alt={`${project.title} - Image ${selectedImage + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />

            {/* Image Counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm">
              {selectedImage + 1} / {project.gallery.length}
            </div>

            {/* Thumbnail Navigation */}
            <div className="absolute -bottom-20 left-1/2 transform -translate-x-1/2 flex space-x-2 max-w-full overflow-x-auto px-4">
              {project.gallery.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 flex-shrink-0 ${
                    index === selectedImage 
                      ? 'bg-orange-500 scale-125' 
                      : 'bg-white/50 hover:bg-white/75'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;